import { useQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  Descriptions,
  Grid,
  Input,
  Modal,
  Progress,
  Select,
  Spin,
  Steps,
  Tag,
  Typography,
  notification,
} from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ONBOARDING_STAGE_KEYS,
  PartnerOnboardingStages,
  PartnerOnboardingStatuses,
  partnerTypeLabel,
  allOnboardingTaskTemplatesQuery,
  extractAllOnboardingTaskTemplates,
  extractPartnerOnboardingsByContact,
  partnerOnboardingsByContactQuery,
  cancelPartnerOnboardingMutation,
  reopenPartnerOnboardingMutation,
  runOnboardingAutoChecksMutation,
  submitOnboardingTaskResponseMutation,
  updateOnboardingTaskNotesMutation,
} from '../../partner-onboarding/constants';
import CreatePartnerOnboardingModal from '../../partner-onboarding/CreatePartnerOnboardingModal';
import TaskResponseItem from '../../partner-onboarding/ViewPartnerOnboardingModal/TaskResponseItem';

const { Title, Text } = Typography;
const { Step } = Steps;

function botTaskHint(taskName, contact) {
  const name = (taskName || '').toLowerCase();
  if (name.includes('email') && name.includes('phone')) {
    const hasEmail = contact?.emails?.length > 0;
    const hasPhone = contact?.phoneNumbers?.length > 0;
    if (!hasEmail && !hasPhone)
      return 'No email or phone on file — add them in the Info tab.';
    if (!hasEmail) return 'No email address on file — add one in the Info tab.';
    if (!hasPhone) return 'No phone number on file — add one in the Info tab.';
    return 'Contact has email and phone — run Auto-checks to complete.';
  }
  if (name.includes('strategy guide')) {
    return 'No strategy guide uploaded yet — upload one in the Strategy Guide tab.';
  }
  return 'This task will be completed automatically when conditions are met.';
}

function PartnerOnboardingTab({ contactId, contact }) {
  const [creating, setCreating] = useState(false);
  const [activeStage, setActiveStage] = useState(1);
  const screens = Grid.useBreakpoint();
  const hasRunChecks = useRef(false);
  const stageInitialized = useRef(false);

  const [selectedId, setSelectedId] = useState(null);
  const { data, loading, refetch } = useQuery(partnerOnboardingsByContactQuery, {
    variables: { contactId },
    skip: !contactId,
    fetchPolicy: 'cache-and-network',
  });
  const onboardings = useMemo(() => extractPartnerOnboardingsByContact(data), [data]);
  // The record on screen: the explicitly selected round, else the active one, else latest.
  const record = useMemo(() => {
    if (!onboardings.length) return null;
    const chosen = selectedId && onboardings.find((o) => o._id === selectedId);
    return chosen || onboardings.find((o) => o.status === 'IN_PROGRESS') || onboardings[0];
  }, [onboardings, selectedId]);

  const { data: templatesData } = useQuery(allOnboardingTaskTemplatesQuery, {
    variables: { first: 200, filters: { isActive: true } },
    skip: !record,
    fetchPolicy: 'cache-first',
  });
  const templates = useMemo(() => {
    const templatesEdges = extractAllOnboardingTaskTemplates(templatesData);
    return (templatesEdges?.edges || []).map((e) => e.node);
  }, [templatesData]);

  const [submitResponse] = useMutation(submitOnboardingTaskResponseMutation);
  const [runAutoChecks, { loading: autoChecking }] = useMutation(runOnboardingAutoChecksMutation);
  const [updateTaskNotes] = useMutation(updateOnboardingTaskNotesMutation);
  const [reopenOnboarding, { loading: reopening }] = useMutation(reopenPartnerOnboardingMutation);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [cancelOnboarding, { loading: cancelling }] = useMutation(cancelPartnerOnboardingMutation);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Run auto-checks once when an active record first loads. The mutation returns the
  // full record, so Apollo's normalized cache updates the view — no refetch needed.
  useEffect(() => {
    if (record && record.status === 'IN_PROGRESS' && !hasRunChecks.current) {
      hasRunChecks.current = true;
      runAutoChecks({ variables: { _id: record._id } }).catch(() => {});
    }
  }, [record, runAutoChecks]);

  const handleSubmitResponse = useCallback(
    async (templateId, answer) => {
      try {
        await submitResponse({
          variables: { input: { _id: record._id, templateId, answer } },
        });
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
    },
    [submitResponse, record],
  );

  const handleAutoChecks = useCallback(async () => {
    try {
      await runAutoChecks({ variables: { _id: record._id } });
      notification.success({ message: 'Auto-checks complete' });
    } catch (err) {
      notification.error({ message: 'Error', description: err.message });
    }
  }, [runAutoChecks, record]);

  const handleUpdateNotes = useCallback(
    async (templateId, notes) => {
      try {
        await updateTaskNotes({
          variables: { input: { _id: record._id, templateId, notes } },
        });
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
    },
    [updateTaskNotes, record],
  );

  const handleReopen = useCallback(async () => {
    try {
      await reopenOnboarding({
        variables: { input: { _id: record._id, reason: reopenReason } },
      });
      notification.success({ message: 'Onboarding reopened' });
      setReopenOpen(false);
      setReopenReason('');
    } catch (err) {
      notification.error({ message: 'Error', description: err.message });
    }
  }, [reopenOnboarding, record, reopenReason]);

  const handleCancel = useCallback(async () => {
    try {
      await cancelOnboarding({
        variables: { input: { _id: record._id, reason: cancelReason } },
      });
      notification.success({ message: 'Onboarding cancelled' });
      setCancelOpen(false);
      setCancelReason('');
    } catch (err) {
      notification.error({ message: 'Error', description: err.message });
    }
  }, [cancelOnboarding, record, cancelReason]);

  const stageTasks = useMemo(
    () =>
      templates.filter(
        (t) =>
          t.stageNumber === activeStage &&
          (!t.partnerTypeCodes?.length ||
            t.partnerTypeCodes.includes(record?.partnerType)),
      ),
    [templates, activeStage, record],
  );

  const responseMap = useMemo(
    () =>
      Object.fromEntries(
        (record?.taskResponses || []).map((r) => [r.templateId, r]),
      ),
    [record],
  );

  // A stage with every applicable task answered renders as `finish` rather than `wait`,
  // so a completed Stage 1 doesn't look identical to an untouched Stage 4. A stage with
  // no applicable tasks is not "finished" -- it just doesn't apply to this partner type.
  const completedStages = useMemo(() => {
    const applicable = templates.filter(
      (t) =>
        !t.partnerTypeCodes?.length ||
        t.partnerTypeCodes.includes(record?.partnerType),
    );
    return new Set(
      ONBOARDING_STAGE_KEYS.filter((s) => {
        const tasksForStage = applicable.filter((t) => t.stageNumber === s);
        return (
          tasksForStage.length > 0 &&
          tasksForStage.every((t) => responseMap[t._id]?.completedAt)
        );
      }),
    );
  }, [templates, record, responseMap]);

  // Open on the current stage (earliest with an incomplete task), not always stage 1.
  useEffect(() => {
    if (stageInitialized.current || !record || !templates.length) return;
    const applicable = templates.filter(
      (t) => !t.partnerTypeCodes?.length || t.partnerTypeCodes.includes(record.partnerType),
    );
    const firstIncomplete = ONBOARDING_STAGE_KEYS.find((s) =>
      applicable.some((t) => t.stageNumber === s && !responseMap[t._id]?.completedAt),
    );
    stageInitialized.current = true;
    if (firstIncomplete) setActiveStage(firstIncomplete);
  }, [record, templates, responseMap]);

  const answeredCount = stageTasks.filter((t) => responseMap[t._id]?.answer).length;
  const statusInfo =
    PartnerOnboardingStatuses[record?.status] || PartnerOnboardingStatuses.IN_PROGRESS;
  const tasksDone = record?.tasksDoneCount ?? 0;
  const tasksTotal = record?.tasksTotalCount ?? 0;
  const isComplete = record?.status === 'COMPLETE';
  const isCancelled = record?.status === 'CANCELLED';
  const reopenHistory = record?.reopenHistory || [];
  const lastReopen = reopenHistory[reopenHistory.length - 1];

  if (loading && !onboardings.length) {
    return <Spin style={{ display: 'block', margin: '40px auto' }} />;
  }

  if (!onboardings.length) {
    return (
      <div style={{ maxWidth: 520, margin: '40px auto', textAlign: 'center' }}>
        <Title level={4} style={{ marginBottom: 8 }}>
          No Onboarding Record
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          This contact doesn&apos;t have a partner onboarding record yet.
        </Text>
        <Button type="primary" onClick={() => setCreating(true)}>
          Start Onboarding
        </Button>
        <CreatePartnerOnboardingModal
          visible={creating}
          initialContactId={contactId}
          onCancel={() => setCreating(false)}
          onSuccess={() => {
            setCreating(false);
            refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {onboardings.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ marginRight: 8 }}>
            Onboarding round:
          </Text>
          <Select
            value={record._id}
            onChange={(v) => {
              setSelectedId(v);
              stageInitialized.current = false;
            }}
            style={{ minWidth: 340 }}
            options={onboardings.map((o) => ({
              value: o._id,
              label: `${partnerTypeLabel(o.partnerType) || 'Partner'} — ${
                PartnerOnboardingStatuses[o.status]?.label || o.status
              }${o.createdAt ? ` — ${new Date(o.createdAt).toLocaleDateString()}` : ''}`,
            }))}
          />
        </div>
      )}
      {isCancelled && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Onboarding cancelled"
          description={
            <span>
              {record.cancellationReason ? `“${record.cancellationReason}”` : null}
              {record.onboardingCancelledDate
                ? ` — ${new Date(record.onboardingCancelledDate).toLocaleString()}`
                : null}
            </span>
          }
          action={
            <Button size="small" type="primary" onClick={() => setCreating(true)}>
              Start New Onboarding
            </Button>
          }
        />
      )}
      {isComplete && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message="Onboarding complete — checklist locked"
          description="Reopen it to make changes (a reason is required)."
          action={
            <Button size="small" onClick={() => setReopenOpen(true)}>
              Reopen
            </Button>
          }
        />
      )}
      {lastReopen && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Reopened ${reopenHistory.length} time${reopenHistory.length > 1 ? 's' : ''}`}
          description={
            <span>
              Last: {new Date(lastReopen.reopenedAt).toLocaleString()} — “{lastReopen.reason}”
            </span>
          }
        />
      )}
      <Descriptions
        bordered
        size="small"
        column={{ xs: 1, sm: 2 }}
        style={{ marginBottom: 20 }}
      >
        <Descriptions.Item label="Region">
          {record.regionName || record.region}
        </Descriptions.Item>
        <Descriptions.Item label="Partner Type">
          {partnerTypeLabel(record.partnerType) || '—'}
        </Descriptions.Item>
        {record.coordinatorContactId && (
          <Descriptions.Item label="Coordinator" span={2}>
            {/* The coordinator is a different contact than the page we're on. */}
            {record.coordinatorContact?.fullName ? (
              record.canViewCoordinatorContact ? (
                <Link
                  to={`/contact/view/${record.coordinatorContactId}/partner-onboarding`}
                >
                  {record.coordinatorContact.fullName}
                </Link>
              ) : (
                record.coordinatorContact.fullName
              )
            ) : (
              record.coordinatorContactId
            )}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Status">
          <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Progress">
          <Progress
            percent={tasksTotal ? Math.round((tasksDone / tasksTotal) * 100) : 0}
            size="small"
            format={() => `${tasksDone}/${tasksTotal}`}
            status={record.status === 'COMPLETE' ? 'success' : 'normal'}
            style={{ maxWidth: 220 }}
          />
        </Descriptions.Item>
        {record.notes && (
          <Descriptions.Item label="Notes" span={2}>
            {record.notes}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Steps
        current={activeStage - 1}
        onChange={(idx) => setActiveStage(idx + 1)}
        size="small"
        direction={screens.sm ? 'horizontal' : 'vertical'}
        style={{ marginBottom: 28, cursor: 'pointer' }}
      >
        {ONBOARDING_STAGE_KEYS.map((s) => (
          <Step
            key={s}
            title={PartnerOnboardingStages[s]?.label}
            status={
              activeStage === s
                ? 'process'
                : completedStages.has(s)
                ? 'finish'
                : 'wait'
            }
          />
        ))}
      </Steps>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          {PartnerOnboardingStages[activeStage]?.label} Tasks
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {answeredCount} / {stageTasks.length} answered
        </Text>
        <Button
          size="small"
          icon={<RobotOutlined />}
          onClick={handleAutoChecks}
          loading={autoChecking}
        >
          Re-check Auto Tasks
        </Button>
        {record.status === 'IN_PROGRESS' && (
          <Button size="small" danger onClick={() => setCancelOpen(true)}>
            Cancel Onboarding
          </Button>
        )}
      </div>

      {stageTasks.length === 0 && (
        <Text type="secondary">No tasks defined for this stage.</Text>
      )}

      {stageTasks.map((template) => {
        const isBot = template.assignedRole === 'BOT';
        const response = responseMap[template._id];
        if (isBot && !response?.completedAt) {
          return (
            <Alert
              key={template._id}
              type="warning"
              icon={<RobotOutlined />}
              showIcon
              style={{ marginBottom: 8 }}
              message={template.taskName}
              description={botTaskHint(template.taskName, contact)}
            />
          );
        }
        return (
          <TaskResponseItem
            key={`${record._id}:${template._id}`}
            template={template}
            response={response}
            onSubmit={handleSubmitResponse}
            onUpdateNotes={handleUpdateNotes}
            isReadOnly={isBot || isComplete || isCancelled}
            contactName={contact?.fullName}
            contactId={contactId}
            partnerType={record.partnerType}
            onDataChanged={refetch}
            defaultAnswer="No"
          />
        );
      })}

      <Modal
        open={reopenOpen}
        title="Reopen onboarding"
        okText="Reopen"
        okButtonProps={{ disabled: !reopenReason.trim(), loading: reopening }}
        onOk={handleReopen}
        onCancel={() => setReopenOpen(false)}
        destroyOnClose
      >
        <Text type="secondary">
          This sets the onboarding back to In Progress and records who reopened it and why.
        </Text>
        <Input.TextArea
          rows={3}
          value={reopenReason}
          onChange={(e) => setReopenReason(e.target.value)}
          placeholder="Why are you reopening this onboarding? (required)"
          style={{ marginTop: 8 }}
        />
      </Modal>

      <Modal
        open={cancelOpen}
        title="Cancel onboarding"
        okText="Cancel onboarding"
        okType="danger"
        okButtonProps={{ disabled: !cancelReason.trim(), loading: cancelling }}
        cancelText="Keep onboarding"
        onOk={handleCancel}
        onCancel={() => setCancelOpen(false)}
        destroyOnClose
      >
        <Text type="secondary">
          This stops the onboarding and records the reason. To onboard this contact
          again later, start a new onboarding (this record is kept for history).
        </Text>
        <Input.TextArea
          rows={3}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Why are you cancelling this onboarding? (required)"
          style={{ marginTop: 8 }}
        />
      </Modal>

      <CreatePartnerOnboardingModal
        visible={creating}
        initialContactId={contactId}
        onCancel={() => setCreating(false)}
        onSuccess={() => {
          setCreating(false);
          refetch();
        }}
      />
    </div>
  );
}

export default PartnerOnboardingTab;
